/**
 * Keyboard-aware inset hook using visualViewport API
 *
 * Sets CSS variable --kb to keyboard overlap height in pixels.
 * Battle-tested on iOS Safari + Android Chrome.
 *
 * WKWebView fix: In-app browsers (iMessage, etc.) resize the webview
 * when the keyboard opens, so window.innerHeight shrinks with vv.height
 * and the primary overlap calculation returns ~0. We track a fullHeight
 * ref (the known viewport height before keyboard) and use it as a
 * fallback to correctly detect keyboard presence.
 */

import { useEffect, useRef } from "react";

export function useKeyboardInset() {
  const fullHeight = useRef(0);

  useEffect(() => {
    const root = document.documentElement as HTMLElement;
    const vv = (window as any).visualViewport as VisualViewport | undefined;

    if (!vv) {
      // Old browsers: footer stays bottom-docked, no keyboard adjustment
      return;
    }

    let debounceTimer: number | null = null;
    let keyboardExpected = false;

    // Track whether a keyboard is likely open via focus events
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
        keyboardExpected = true;
      }
    };

    const onFocusOut = () => {
      keyboardExpected = false;
      // When keyboard closes, update fullHeight to current dimensions
      // (use a short delay so the viewport has finished resizing)
      setTimeout(() => {
        if (!keyboardExpected) {
          fullHeight.current = Math.max(fullHeight.current, window.innerHeight, vv.height);
        }
      }, 300);
    };

    // Initialize fullHeight
    fullHeight.current = Math.max(window.innerHeight, vv.height);

    const updateKeyboardInset = () => {
      // Debounce rapid updates to prevent flickering
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      debounceTimer = setTimeout(() => {
        // Only ratchet fullHeight upward when no keyboard is expected
        if (!keyboardExpected) {
          fullHeight.current = Math.max(fullHeight.current, window.innerHeight, vv.height);
        }

        // Primary: works in regular Safari where window.innerHeight stays large
        const primaryOverlap = window.innerHeight - vv.height - vv.offsetTop;
        // Fallback: works in WKWebView where window.innerHeight shrinks with keyboard
        const fallbackOverlap = fullHeight.current - vv.height - vv.offsetTop;

        const overlap = Math.max(0, primaryOverlap, fallbackOverlap);
        const newValue = `${Math.round(overlap)}px`;

        // Only update if value actually changed to prevent unnecessary reflows
        const currentValue = root.style.getPropertyValue("--kb");
        if (currentValue !== newValue) {
          root.style.setProperty("--kb", newValue);

          // Debug logging for development
          if (import.meta.env.DEV) {
            console.log('Keyboard inset updated:', {
              windowHeight: window.innerHeight,
              visualViewportHeight: vv.height,
              offsetTop: vv.offsetTop,
              fullHeight: fullHeight.current,
              primaryOverlap,
              fallbackOverlap,
              keyboardOverlap: overlap,
              previous: currentValue,
              new: newValue
            });
          }
        }

        debounceTimer = null;
      }, 16); // ~60fps to prevent lag but smooth enough
    };

    // Reset fullHeight on orientation change (dimensions change)
    const onOrientationChange = () => {
      setTimeout(() => {
        fullHeight.current = Math.max(window.innerHeight, vv.height);
        updateKeyboardInset();
      }, 200);
    };

    // Set initial value
    updateKeyboardInset();

    // Listen for keyboard show/hide events
    vv.addEventListener("resize", updateKeyboardInset);
    vv.addEventListener("scroll", updateKeyboardInset);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    window.addEventListener("orientationchange", onOrientationChange);

    return () => {
      // Clear any pending debounce timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      vv.removeEventListener("resize", updateKeyboardInset);
      vv.removeEventListener("scroll", updateKeyboardInset);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      window.removeEventListener("orientationchange", onOrientationChange);
      root.style.removeProperty("--kb");
    };
  }, []);
}
